import type { Injector } from '@angular/core';
import { ApolloLink } from '@apollo/client/core';
import type { Observable } from 'rxjs';
import { finalize, map, shareReplay, throwError } from 'rxjs';
import { APP_CONFIG } from '@shared/tokens/global';
import { environment } from '../../../environments/environment';
import { UserService } from '@features/sign-in/services/user.service';
import { RefreshTokenGQL } from '@graphql/legacy/services';
import { IS_EMBED_LINK } from '@shared/constants/is-embed-link.constant';
import { REFRESH_TOKEN, TOKEN } from '@shared/constants/common.constants';
import { isTokenInvalid } from '@shared/helpers/is-token-invalid';
import { catchError, switchMap } from 'rxjs/operators';
import { isNotDefined } from '@3dsource/utils';

const NO_TOKEN_OPERATIONS = [
  'generateToken',
  'refreshToken',
  'resetPassword',
  'createPasswordResetRequest',
];

export const authLink = (injector: Injector): ApolloLink => {
  let refreshInFlight$: Observable<string> | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return new ApolloLink((operation, forward): any => {
    if (
      NO_TOKEN_OPERATIONS.includes(operation.operationName) ||
      IS_EMBED_LINK ||
      environment.playwright
    ) {
      return forward(operation);
    }

    const userService = injector.get(UserService);
    const config = injector.get(APP_CONFIG);
    const token = userService.getToken(TOKEN(config.tokenPrefix));
    const refreshToken = userService.getToken(
      REFRESH_TOKEN(config.tokenPrefix),
    );
    if (!token && !refreshToken) {
      userService.logout('No token found');
      return forward(operation);
    }
    if (isTokenInvalid(token)) {
      if (isNotDefined(refreshInFlight$)) {
        refreshInFlight$ = injector
          .get(RefreshTokenGQL)
          .mutate({ input: { refreshToken } })
          .pipe(
            map(({ data }) => {
              const tokens = data?.refreshToken;
              if (tokens?.refreshToken) {
                userService.setRefreshToken(tokens.refreshToken);
              }
              if (tokens?.token) {
                userService.setAccessToken(tokens.token);
              }
              return tokens?.token ?? '';
            }),
            catchError((error: unknown) => {
              userService.logout('Refresh failed');
              return throwError(() => error);
            }),
            finalize(() => (refreshInFlight$ = null)),
            shareReplay({ bufferSize: 1, refCount: true }),
          );
      }

      return refreshInFlight$.pipe(
        switchMap((newToken) => {
          operation.setContext({
            headers: {
              Accept: 'application/json; charset=utf-8',
              Authorization: `Bearer ${newToken}`,
            },
          });
          return forward(operation);
        }),
      );
    }
    operation.setContext({
      headers: {
        Accept: 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`,
      },
    });
    return forward(operation);
  });
};
